import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {defineMessage, FormattedMessage, useIntl} from 'react-intl'
import {CheckIcon} from '@salesforce/retail-react-app/app/components/icons'
import {
    Box,
    Button,
    Container,
    SimpleGrid,
    Stack,
    useDisclosure
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useForm, Controller} from 'react-hook-form'
import {shallowEquals} from '@salesforce/retail-react-app/app/utils/utils'
import {PlusIcon} from '@salesforce/retail-react-app/app/components/icons'
import {MESSAGE_PROPTYPE} from '@salesforce/retail-react-app/app/utils/locale'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useShopperCustomersMutation} from '@salesforce/commerce-sdk-react'
import AddressDisplay from '../../../components/address-display'
import FormActionButtons from '../../../components/forms/form-action-button'
import AddressFields from '../../../components/forms/address-fields'
import {pxToRem} from '../../../utills/pxToRem'
import ActionCard from '../../../components/action-card'
import {RadioCard, RadioCardGroup} from '../../../components/radio-card'
import upsAddressValidation from '../../../hooks/ups-address-validation'
import AddressValidationModal from '../../../components/address-validation-modal'
import BopisAddress from './bopis-address'

const saveButtonMessage = defineMessage({
    defaultMessage: 'Save And Continue',
    id: 'shipping_address_edit_form.button.save_continue'
})

const ShippingAddressEditForm = ({
    title,
    hasSavedAddresses,
    hideSubmitButton,
    form,
    submitButtonLabel,
    formTitleAriaLabel,
    isBillingAddress = false
}) => {
    const {formatMessage} = useIntl()

    return (
        <Box
            {...(hasSavedAddresses &&
                !isBillingAddress && {
                    gridColumn: [1, 1, 'span 2']
                })}
            data-testid="sf-shipping-address-edit-form"
        >
            <Stack spacing={6}>
                {!isBillingAddress && (
                    <Box
                        as="p"
                        fontWeight={500}
                        lineHeight={'normal'}
                        letterSpacing={pxToRem(1)}
                        marginBlock={0}
                        marginTop={pxToRem(9)}
                    >
                        {title}
                    </Box>
                )}
                <Stack spacing={6} marginTop={`0 !important`}>
                    <AddressFields
                        form={form}
                        formTitleAriaLabel={formTitleAriaLabel}
                        isBillingAddress={isBillingAddress}
                    />

                    {hasSavedAddresses && !hideSubmitButton ? (
                        <FormActionButtons saveButtonLabel={saveButtonMessage} />
                    ) : (
                        !hideSubmitButton && (
                            <Box>
                                <Container
                                    variant="form"
                                    maxWidth={'100%'}
                                    width={'100%'}
                                    margin={0}
                                >
                                    <Button
                                        type="submit"
                                        width="100%"
                                        className="button-primary"
                                        disabled={form.formState.isSubmitting}
                                    >
                                        {formatMessage(submitButtonLabel)}
                                    </Button>
                                </Container>
                            </Box>
                        )
                    )}
                </Stack>
            </Stack>
        </Box>
    )
}

ShippingAddressEditForm.propTypes = {
    title: PropTypes.string,
    hasSavedAddresses: PropTypes.bool,
    toggleAddressEdit: PropTypes.func,
    hideSubmitButton: PropTypes.bool,
    form: PropTypes.object,
    submitButtonLabel: MESSAGE_PROPTYPE,
    formTitleAriaLabel: MESSAGE_PROPTYPE,
    isBillingAddress: PropTypes.bool
}

const submitButtonMessage = defineMessage({
    defaultMessage: 'Submit',
    id: 'shipping_address_selection.button.submit'
})

const ShippingAddressSelection = ({
    form,
    selectedAddress,
    submitButtonLabel = submitButtonMessage,
    formTitleAriaLabel,
    hideSubmitButton = false,
    onSubmit = async () => null,
    isBillingAddress = false,
    isAllBopisItems = false
}) => {
    const {formatMessage} = useIntl()
    const {data: customer, isLoading, isFetching, refetch} = useCurrentCustomer()
    const isLoadingRegisteredCustomer = isLoading && isFetching

    const hasSavedAddresses = customer.addresses?.length > 0
    const [isEditingAddress, setIsEditingAddress] = useState(false)
    const [selectedAddressId, setSelectedAddressId] = useState(undefined)
    const [enableValidateAddress, setEnableValidateAddress] = useState(false)
    const [enteredAddress, setEnteredAddress] = useState({})
    const {
        isOpen: isOpenStoreSelector,
        onOpen: onOpenStoreSelector,
        onClose: onCloseStoreSelector
    } = useDisclosure()

    // keep track of the edit buttons so we can focus on them later for accessibility
    const [editBtnRefs, setEditBtnRefs] = useState({})
    const {
        data: validatedAddress,
        refetch: refetchCustomerSearch,
        dataUpdatedAt
    } = upsAddressValidation(enteredAddress, enableValidateAddress)

    useEffect(() => {
        const currentRefs = {}
        customer.addresses?.forEach(({addressId}) => {
            currentRefs[addressId] = React.createRef()
        })
        setEditBtnRefs(currentRefs)
    }, [customer.addresses])

    const defaultForm = useForm({
        mode: 'onChange',
        shouldUnregister: false,
        defaultValues: {...selectedAddress}
    })
    if (!form) form = defaultForm

    const hanldeConfiram = (address) => {
        if (Array.isArray(address.address1)) {
            address.address1 = address.address1.join(' ')
        }
        onCloseStoreSelector()
        submitForm(address)
        setEnableValidateAddress(false)
    }

    const hanldeCancel = () => {
        onCloseStoreSelector()
        setEnableValidateAddress(false)
    }

    const handleValidateAddress = (address) => {
        const {address1, city, stateCode, postalCode, countryCode} = address
        setEnteredAddress({
            address1,
            city,
            stateCode,
            postalCode,
            countryCode: (countryCode || 'US').toUpperCase()
        })
        setEnableValidateAddress(true)
    }

    useEffect(() => {
        if (enableValidateAddress && enteredAddress?.address1) {
            refetchCustomerSearch()
        }
    }, [enteredAddress, enableValidateAddress])

    useEffect(() => {
        if (!enableValidateAddress) return
        const XAVResponse = validatedAddress?.UPSResponse?.responseObject?.XAVResponse || {}
        if (Object.prototype.hasOwnProperty.call(XAVResponse, 'NoCandidatesIndicator')) {
            onOpenStoreSelector(true)
        } else if (Object.prototype.hasOwnProperty.call(XAVResponse, 'AmbiguousAddressIndicator')) {
            onOpenStoreSelector(true)
        } else {
            submitForm(form.getValues())
            setEnableValidateAddress(false)
        }
    }, [validatedAddress, dataUpdatedAt])

    const matchedAddress =
        hasSavedAddresses &&
        selectedAddress &&
        customer.addresses.find((savedAddress) => {
            const {addressId, creationDate, lastModified, preferred, ...address} = savedAddress

            const {id, _type, ...selectedAddr} = selectedAddress
            return shallowEquals(address, selectedAddr)
        })
    const removeCustomerAddress = useShopperCustomersMutation('removeCustomerAddress')

    useEffect(() => {
        if (isBillingAddress) {
            form.reset({...selectedAddress})
            return
        }
        // Automatically select the customer's default/preferred shipping address
        if (customer.addresses) {
            const address = customer.addresses.find((addr) => addr.preferred === true)
            if (address) {
                form.reset({...address})
            }
        }
    }, [])

    useEffect(() => {
        // If the customer deletes all their saved addresses during checkout,
        // we need to make sure to display the address form.
        if (!customer?.addresses && !isEditingAddress) {
            setIsEditingAddress(true)
        }
    }, [customer])

    useEffect(() => {
        if (matchedAddress) {
            form.reset({
                addressId: matchedAddress.addressId,
                ...matchedAddress
            })
        }

        if (!matchedAddress && selectedAddressId) {
            setIsEditingAddress(true)
        }
    }, [matchedAddress])

    // Updates the selected customer address if we've an address selected
    // else saves a new customer address
    const submitForm = async (address) => {
        if (selectedAddressId) {
            address = {...address, addressId: selectedAddressId}
        }

        setIsEditingAddress(false)
        form.reset({addressId: ''})

        await onSubmit(address)
    }

    // Acts as our `onChange` handler for addressId radio group. We do this
    // manually here so we can toggle off the 'add address' form as needed.
    const handleAddressIdSelection = (addressId) => {
        if (addressId && isEditingAddress) {
            setIsEditingAddress(false)
        }

        const address = customer.addresses.find((addr) => addr.addressId === addressId)

        form.reset({...address})
    }

    const headingText = formatMessage({
        defaultMessage: 'Shipping Address',
        id: 'shipping_address.title.shipping_address'
    })
    const shippingAddressHeading = Array.from(document.querySelectorAll('h2')).find(
        (element) => element.textContent === headingText
    )

    const removeSavedAddress = async (addressId) => {
        if (addressId === selectedAddressId) {
            setSelectedAddressId(undefined)
            setIsEditingAddress(false)
            form.reset({addressId: ''})
        }

        await removeCustomerAddress.mutateAsync(
            {
                parameters: {
                    customerId: customer.customerId,
                    addressName: addressId
                }
            },
            {
                onSuccess: () => {
                    // Focus on header after successful remove for accessibility
                    shippingAddressHeading?.focus()
                    refetch()
                }
            }
        )
    }

    // Opens/closes the 'add address' form. Notice that when toggling either state,
    // we reset the form so as to remove any address selection.
    const toggleAddressEdit = (address = undefined) => {
        if (address?.addressId) {
            setSelectedAddressId(address.addressId)
            form.reset({...address})
            setIsEditingAddress(true)
        } else {
            // Focus on the edit button that opened the form when the form closes
            // otherwise focus on the heading if we can't find the button
            const focusAfterClose =
                editBtnRefs[selectedAddressId]?.current ?? shippingAddressHeading
            focusAfterClose?.focus()
            setSelectedAddressId(undefined)
            form.reset({addressId: ''})
            setIsEditingAddress(!isEditingAddress)
        }

        form.trigger()
    }

    if (isLoadingRegisteredCustomer) {
        // Don't render anything yet, to make sure values like hasSavedAddresses are correct
        return null
    }
    return (
        <>
            {isAllBopisItems ? (
                <BopisAddress />
            ) : (
                <>
                    {hasSavedAddresses && !isBillingAddress && (
                        <Box
                            display={['none', 'none', 'none', 'block']}
                            fontSize={pxToRem(16)}
                            fontWeight={500}
                            lineHeight={'normal'}
                            letterSpacing={pxToRem(1)}
                            marginBottom={pxToRem(25)}
                            color="black"
                        >
                            {formatMessage({
                                defaultMessage: 'Select a Shipping Address',
                                id: 'shipping_address_selection.label.select_shipping_adds'
                            })}
                        </Box>
                    )}
                    {/* <form onSubmit={form.handleSubmit(submitForm)}> */}
                    <form onSubmit={form.handleSubmit(handleValidateAddress)}>
                        <Stack spacing={4}>
                            {hasSavedAddresses && !isBillingAddress && (
                                <Controller
                                    name="addressId"
                                    defaultValue=""
                                    control={form.control}
                                    rules={{required: !isEditingAddress}}
                                    render={({field: {value}}) => (
                                        <RadioCardGroup
                                            value={value}
                                            onChange={handleAddressIdSelection}
                                        >
                                            <SimpleGrid
                                                columns={[1, 1, 2]}
                                                spacing={4}
                                                gridAutoFlow="row dense"
                                            >
                                                {customer.addresses?.map((address, index) => {
                                                    const editLabel = formatMessage(
                                                        {
                                                            defaultMessage: 'Edit {address}',
                                                            id: 'shipping_address.label.edit_button'
                                                        },
                                                        {address: address.address1}
                                                    )

                                                    const removeLabel = formatMessage(
                                                        {
                                                            defaultMessage: 'Remove {address}',
                                                            id: 'shipping_address.label.remove_button'
                                                        },
                                                        {address: address.address1}
                                                    )
                                                    return (
                                                        <React.Fragment key={address.addressId}>
                                                            <RadioCard value={address.addressId}>
                                                                <ActionCard
                                                                    padding={0}
                                                                    border="none"
                                                                    onRemove={() =>
                                                                        removeSavedAddress(
                                                                            address.addressId
                                                                        )
                                                                    }
                                                                    onEdit={() =>
                                                                        toggleAddressEdit(address)
                                                                    }
                                                                    editBtnRef={
                                                                        editBtnRefs[
                                                                            address.addressId
                                                                        ]
                                                                    }
                                                                    data-testid={`sf-checkout-shipping-address-${index}`}
                                                                    editBtnLabel={editLabel}
                                                                    removeBtnLabel={removeLabel}
                                                                    enableShippingAddressDelete={
                                                                        customer?.addresses
                                                                            ?.length > 1
                                                                    }
                                                                >
                                                                    <AddressDisplay
                                                                        isCountryAdded={true}
                                                                        address={address}
                                                                    />
                                                                </ActionCard>
                                                            </RadioCard>
                                                            {isEditingAddress &&
                                                                address.addressId ===
                                                                    selectedAddressId && (
                                                                    <ShippingAddressEditForm
                                                                        title={formatMessage({
                                                                            defaultMessage:
                                                                                'Edit Shipping Address',
                                                                            id: 'shipping_address_selection.label.edit_address'
                                                                        })}
                                                                        hasSavedAddresses={
                                                                            hasSavedAddresses
                                                                        }
                                                                        toggleAddressEdit={
                                                                            toggleAddressEdit
                                                                        }
                                                                        hideSubmitButton={
                                                                            hideSubmitButton
                                                                        }
                                                                        form={form}
                                                                        submitButtonLabel={
                                                                            submitButtonLabel
                                                                        }
                                                                        formTitleAriaLabel={
                                                                            formTitleAriaLabel
                                                                        }
                                                                    />
                                                                )}
                                                        </React.Fragment>
                                                    )
                                                })}
                                                <Box position="relative">
                                                    <Button
                                                        variant="outline"
                                                        border={
                                                            isEditingAddress && !selectedAddressId
                                                                ? '1px solid'
                                                                : '1px dashed'
                                                        }
                                                        borderColor={
                                                            isEditingAddress && !selectedAddressId
                                                                ? 'cav_calltoaction_darkred'
                                                                : 'cav_light_input_gray'
                                                        }
                                                        borderRadius={0}
                                                        color="cav_red"
                                                        height={['44px', '44px', '100%']}
                                                        minHeight={{
                                                            base: pxToRem(44),
                                                            md: pxToRem(167)
                                                        }}
                                                        rounded="base"
                                                        fontWeight="700"
                                                        lineHeight={pxToRem(13.5)}
                                                        textTransform={'uppercase'}
                                                        letterSpacing={pxToRem(1.68)}
                                                        width={'100%'}
                                                        fontSize={{
                                                            base: pxToRem(12),
                                                            lg: pxToRem(14)
                                                        }}
                                                        leftIcon={
                                                            <PlusIcon
                                                                boxSize={'18px'}
                                                                fontWeight={700}
                                                            />
                                                        }
                                                        onClick={toggleAddressEdit}
                                                    >
                                                        <FormattedMessage
                                                            defaultMessage="Add Address"
                                                            id="shipping_address_selection.button.add_addres"
                                                        />
                                                    </Button>
                                                    {isEditingAddress && !selectedAddressId && (
                                                        <Box
                                                            position="absolute"
                                                            top={0}
                                                            right={0}
                                                            w={0}
                                                            h={0}
                                                            borderStyle="solid"
                                                            borderWidth="0 38px 38px 0"
                                                            borderColor="transparent"
                                                            borderRightColor="cav_calltoaction_darkred"
                                                        >
                                                            <CheckIcon
                                                                color="white"
                                                                position="absolute"
                                                                right="-40px"
                                                                top="1px"
                                                            />
                                                        </Box>
                                                    )}
                                                </Box>
                                            </SimpleGrid>
                                        </RadioCardGroup>
                                    )}
                                />
                            )}

                            {(customer?.isGuest ||
                                (isEditingAddress && !selectedAddressId) ||
                                isBillingAddress) && (
                                <ShippingAddressEditForm
                                    title={formatMessage({
                                        defaultMessage: 'Enter Shipping Address',
                                        id: 'shipping_address_selection.title.enter_address'
                                    })}
                                    hasSavedAddresses={hasSavedAddresses}
                                    toggleAddressEdit={toggleAddressEdit}
                                    hideSubmitButton={hideSubmitButton}
                                    form={form}
                                    isBillingAddress={isBillingAddress}
                                    submitButtonLabel={submitButtonLabel}
                                    formTitleAriaLabel={formTitleAriaLabel}
                                />
                            )}

                            {customer.isRegistered && !isEditingAddress && !hideSubmitButton && (
                                <Box pt={2}>
                                    <Container
                                        variant="form"
                                        width={'100%'}
                                        margin={0}
                                        maxWidth="100%"
                                    >
                                        <Button
                                            type="submit"
                                            width="100%"
                                            className="button-primary"
                                            disabled={
                                                !form.formState.isValid ||
                                                form.formState.isSubmitting
                                            }
                                        >
                                            {formatMessage(submitButtonLabel)}
                                        </Button>
                                    </Container>
                                </Box>
                            )}
                        </Stack>
                    </form>
                    {/* Validate address modal */}
                    <AddressValidationModal
                        address={form.getValues()}
                        isOpen={isOpenStoreSelector}
                        onClose={hanldeCancel}
                        hanldeConfiram={hanldeConfiram}
                        hanldeCancel={hanldeCancel}
                        Candidate={
                            validatedAddress?.UPSResponse?.responseObject?.XAVResponse?.Candidate
                        }
                        // eslint-disable-next-line no-prototype-builtins
                        NoCandidatesIndicator={validatedAddress?.UPSResponse?.responseObject?.XAVResponse?.hasOwnProperty(
                            'AmbiguousAddressIndicator'
                        )}
                    />
                </>
            )}
        </>
    )
}

ShippingAddressSelection.propTypes = {
    form: PropTypes.object,
    selectedAddress: PropTypes.object,
    submitButtonLabel: MESSAGE_PROPTYPE,
    formTitleAriaLabel: MESSAGE_PROPTYPE,
    hideSubmitButton: PropTypes.bool,
    onSubmit: PropTypes.func,
    isBillingAddress: PropTypes.bool,
    isAllBopisItems: PropTypes.bool
}

export default ShippingAddressSelection
